import { Card, Flex, Typography } from 'antd';
import React, { useEffect, useState, Suspense, lazy } from 'react';
import Icon from '@ant-design/icons';
import { ReactComponent as MoreSvg } from '../../../../assets/icons/more.svg';
import { ReactComponent as MoreBlackSvg } from '../../../../assets/icons/more-black.svg';
import Legend from './Legend';
import { useTheme } from '../../../../context/ThemeContext';
import {
  CHART_BG_COLOR,
  CHART_MARGINS,
  DARK,
  SOS_STATUS_KEYS,
  SOS_STATUS_KEYS_TEXT,
} from '../../../../utilities/constants';
import { SuspenseFallback } from '../../../../components/SuspenseFallback/SuspenseFallback';
import PropTypes from 'prop-types';

// Lazy load the Plot component
const Plot = lazy(() => import('../../../../components/Plot/Plot'));

const { Text } = Typography;

const getResolvedColor = (variableName, fallback) => {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(variableName)
    .trim();
  return value || fallback;
};

const getSOSColorVar = (base, isSelected, isHov) => {
  if (isSelected) return `${base}-selected`;
  if (isHov) return `${base}-hovered`;
  return base;
};

const getSOSFallbackColor = (
  selectedVal,
  hoveredVal,
  defaultVal,
  isSelected,
  isHov
) => {
  if (isSelected) return selectedVal;
  if (isHov) return hoveredVal;
  return defaultVal;
};

const getMoreIcon = (isSelected, isDark) => {
  if (isSelected) return isDark ? MoreBlackSvg : MoreSvg;
  return isDark ? MoreSvg : MoreBlackSvg;
};

const getCardBackground = (isSelected, isDark) => {
  if (isSelected) return 'var(--selected-bg-color, #D0D4D8)';
  return isDark ? '#2E3033' : '#E0E2E5';
};

const SOSAlertsCard = ({ selectedTabKey, setSelectedTabKey, sosListData }) => {
  const { theme } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const isSelected = selectedTabKey === 0;
  const isDark = theme === DARK;
  const selectedTextColor = isDark ? '#000000' : '#FFFFFF';
  const defaultTextColor = isDark ? '#FFFFFF' : '#000000';
  const chartTextColor = isSelected ? selectedTextColor : defaultTextColor;
  const [totalCount, setTotalCount] = useState(0);

  const pendingColor = getResolvedColor(
    getSOSColorVar('--sos-ticket-new', isSelected, isHovered),
    getSOSFallbackColor('#ED0007', '#BE0004', '#FF2124', isSelected, isHovered)
  );
  const inProgressColor = getResolvedColor(
    getSOSColorVar('--sos-ticket-inprogress', isSelected, isHovered),
    getSOSFallbackColor('#007BC0', '#00629A', '#418BD6', isSelected, isHovered)
  );
  const resolvedColor = getResolvedColor(
    getSOSColorVar('--sos-ticket-resolved', isSelected, isHovered),
    getSOSFallbackColor('#00884A', '#006C3A', '#1DAA00', isSelected, isHovered)
  );

  const initialPlotData = {
    values: [0, 0, 0],
    labels: ['Pending', 'In progress', 'Resolved'],
    type: 'pie',
    hole: 0.75,
    textinfo: 'value',
    textposition: 'outside',
    textfont: {
      size: 16,
      color: defaultTextColor,
      weight: 700,
    },
    automargin: true,
    marker: {
      colors: [pendingColor, inProgressColor, resolvedColor],
    },
  };

  const initialLayout = {
    showlegend: false,
    paper_bgcolor: CHART_BG_COLOR,
    plot_bgcolor: CHART_BG_COLOR,
    margin: CHART_MARGINS,
    height: 240,
    annotations: [
      {
        font: {
          color: chartTextColor,
          size: 32,
        },
        showarrow: false,
        text: `<b>${totalCount || 0}</b>`,
        x: 0.5,
        y: 0.5,
      },
    ],
  };

  const [plotData, setPlotData] = useState([initialPlotData]);
  const [layout, setLayout] = useState(initialLayout);

  useEffect(() => {
    setPlotData(prev =>
      prev.map(item => ({
        ...item,
        marker: {
          ...item.marker,
          colors: [pendingColor, inProgressColor, resolvedColor],
        },
      }))
    );
  }, [theme, selectedTabKey, pendingColor, inProgressColor, resolvedColor]);

  useEffect(() => {
    setLayout(prev => ({
      ...prev,
      annotations: [
        {
          font: {
            color: chartTextColor,
            size: 32,
          },
          showarrow: false,
          text: `<b>${totalCount || 0}</b>`,
          x: 0.5,
          y: 0.5,
        },
      ],
    }));
  }, [totalCount, chartTextColor]);

  useEffect(() => {
    setPlotData(prev =>
      prev.map(item => ({
        ...item,
        textfont: {
          ...item.textfont,
          color: chartTextColor,
        },
      }))
    );
  }, [chartTextColor]);

  useEffect(() => {
    setPlotData(prev =>
      prev.map(plot => ({
        ...plot,
        values: [
          sosListData?.[SOS_STATUS_KEYS?.PENDING]?.length ?? 0,
          sosListData?.[SOS_STATUS_KEYS?.IN_PROGRESS]?.length ?? 0,
          sosListData?.[SOS_STATUS_KEYS?.RESOLVED]?.length ?? 0,
        ],
      }))
    );

    setTotalCount(
      (sosListData?.[SOS_STATUS_KEYS?.PENDING]?.length ?? 0) +
        (sosListData?.[SOS_STATUS_KEYS?.IN_PROGRESS]?.length ?? 0) +
        (sosListData?.[SOS_STATUS_KEYS?.RESOLVED]?.length ?? 0)
    );
  }, [sosListData]);

  return (
    <Card
      size="small"
      variant="borderless"
      onClick={() => setSelectedTabKey(0)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`card-safety-anomaly-chart card-safety-anomaly-chart-${isSelected ? 'selected' : 'not-selected'}`}
      style={{
        background: getCardBackground(isSelected, isDark),
        color: isSelected ? selectedTextColor : defaultTextColor,
        height: '100%',
      }}
    >
      <Flex vertical gap="large" className="full-height">
        <Flex justify="space-between">
          <Text
            className="card-title"
            style={{
              color: isSelected ? selectedTextColor : defaultTextColor,
            }}
          >
            SOS Alerts
          </Text>
          <Icon
            component={getMoreIcon(isSelected, isDark)}
            className="card-title"
          />
        </Flex>
        <Flex
          vertical
          gap={20}
          align="center"
          justify="center"
          className="full-height"
        >
          <Suspense
            fallback={<SuspenseFallback height={'240px'} width={'100%'} />}
          >
            <Plot
              data={plotData}
              layout={layout}
              className="full-width"
              style={{
                marginTop: 0,
              }}
              config={{
                responsive: true,
                displayModeBar: false,
              }}
            />
          </Suspense>
          <Legend
            items={[
              {
                key: 'pending',
                label: SOS_STATUS_KEYS_TEXT.PENDING,
                color: pendingColor,
              },

              {
                key: 'in progress',
                label: SOS_STATUS_KEYS_TEXT.IN_PROGRESS,
                color: inProgressColor,
              },
              {
                key: 'resolved',
                label: SOS_STATUS_KEYS_TEXT.RESOLVED,
                color: resolvedColor,
              },
            ]}
            color={isSelected ? selectedTextColor : defaultTextColor}
          />
        </Flex>
      </Flex>
    </Card>
  );
};

SOSAlertsCard.propTypes = {
  selectedTabKey: PropTypes.number,
  setSelectedTabKey: PropTypes.func.isRequired,
  sosListData: PropTypes.object,
};

export default React.memo(SOSAlertsCard);
